import { useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'

import { useFetch } from '@reveni-io/reveni-ui'

import inventoryServices from '@/inventory/services/services'

import { getBulkUpdatePayload } from './helpers'

const useFixInventoryItems = ({
  refetchItems,
  storeId,
  setToastInfo,
  fetchInventoryMetrics,
  selectedProductIds,
  setSelectedProducts,
}) => {
  const { t } = useTranslation()
  const [showFixInventoryItemsModal, setShowFixInventoryItemsModal] = useState(false)
  const [showFixInventoryItemsDrawer, setShowFixInventoryItemsDrawer] = useState(false)
  const { loading: startingAutoFix, fetchData: startAutoFix } = useFetch(inventoryServices.startAutoFix, {
    doInitialRequest: false,
  })
  const websocketRef = useRef(null)
  const [draftItems, setDraftItems] = useState([])
  const [progressItems, setProgressItems] = useState({ completed: 0, total: 0 })
  const { loading: confirmingChanges, fetchData: confirmChanges } = useFetch(
    inventoryServices.inventoryItemsBulkUpdate,
    {
      doInitialRequest: false,
    }
  )

  const openWebSocket = url => {
    if (websocketRef.current) {
      websocketRef.current.close()
    }

    websocketRef.current = new WebSocket(url)

    websocketRef.current.onmessage = async event => {
      const response = JSON.parse(event.data)

      if (response?.content?.results?.length) {
        setProgressItems({
          completed: response.content.completed,
          total: response.content.total,
        })
        setDraftItems(items => [
          ...items,
          ...response.content.results.map((item, index) => ({ ...item, delay: index * 0.2 })),
        ])
      }
    }

    websocketRef.current.onerror = () => {
      setToastInfo({
        type: 'error',
        message: t('Error al conectar al WebSocket'),
      })
      setDraftItems([])
      websocketRef.current?.close()
    }
  }

  const openFixInventoryItemsModal = () => {
    setShowFixInventoryItemsModal(true)
  }

  const closeFixInventoryItemsModal = () => {
    setShowFixInventoryItemsModal(false)
  }

  const onStartFixInventoryItems = async () => {
    setShowFixInventoryItemsModal(false)
    setShowFixInventoryItemsDrawer(true)

    try {
      const data = await startAutoFix({ storeId, payload: { product_ids: selectedProductIds } })

      openWebSocket(data.websocket_url)
    } catch {
      setToastInfo({
        type: 'error',
        message: t('Error al corregir los artículos del inventario'),
      })
    }
  }

  const closeFixInventoryItemsDrawer = () => {
    setShowFixInventoryItemsDrawer(false)
    websocketRef.current?.close()
    setDraftItems([])
    setProgressItems({ completed: 0, total: 0 })
  }

  const onConfirmChanges = async acceptedItems => {
    const payload = getBulkUpdatePayload(acceptedItems)
    await confirmChanges({ storeId, payload })
    setToastInfo({
      type: 'success',
      message: t('Cambios confirmados correctamente'),
    })
    closeFixInventoryItemsDrawer()
    setSelectedProducts([])
    refetchItems()
    fetchInventoryMetrics()
  }

  return {
    draftItems,
    startingAutoFix,
    showFixInventoryItemsModal,
    openFixInventoryItemsModal,
    closeFixInventoryItemsModal,
    showFixInventoryItemsDrawer,
    onStartFixInventoryItems,
    closeFixInventoryItemsDrawer,
    progressItems,
    onConfirmChanges,
    confirmingChanges,
    openWebSocket,
  }
}

export default useFixInventoryItems
